<template>
  <div
    class="flex gap-2 py-1 group relative"
    :class="isSelf ? 'flex-row-reverse' : 'flex-row'"
    @contextmenu.prevent="handleContextMenu"
  >
    <!-- 头像 -->
    <img
      v-if="!isSelf && displayAvatar"
      :src="displayAvatar"
      class="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5"
    />
    <div
      v-else-if="!isSelf"
      class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0 mt-0.5"
      :class="avatarColor"
    >
      {{ displayName.slice(-1) }}
    </div>

    <!-- 消息内容 -->
    <div :class="isSelf ? 'items-end' : 'items-start'" class="flex flex-col max-w-[65%]">
      <!-- 发送者名称（群聊时显示） -->
      <span v-if="showSender && !isSelf" class="text-[11px] text-slate-400 mb-0.5 ml-1">
        {{ displayName }}
      </span>

      <!-- 引用回复区域 -->
      <div
        v-if="message.replyTo"
        class="px-2.5 py-1.5 mb-0.5 rounded-lg text-[11px] max-w-full cursor-pointer"
        :class="isSelf ? 'bg-primary-400/40 text-primary-100' : 'bg-slate-100 text-slate-500'"
      >
        <span class="font-medium">{{ message.replyTo.senderName }}</span>
        <p class="truncate">{{ message.replyTo.content }}</p>
      </div>

      <!-- 消息气泡 + 操作菜单 -->
      <div class="relative flex items-center gap-1" :class="isSelf ? 'flex-row-reverse' : 'flex-row'">
        <!-- 文本消息 -->
        <div
          v-if="message.type === 'text'"
          class="px-3 py-2 rounded-xl text-sm leading-relaxed whitespace-pre-wrap break-words"
          :class="isSelf
            ? 'bg-primary-500 text-white rounded-br-sm'
            : 'bg-white text-slate-700 border border-slate-200/80 rounded-bl-sm shadow-sm'"
        >
          {{ message.content }}
        </div>

        <!-- 文件消息 -->
        <div
          v-else-if="message.type === 'file'"
          class="px-3 py-2.5 rounded-xl flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow"
          :class="isSelf
            ? 'bg-primary-500 text-white rounded-br-sm'
            : 'bg-white text-slate-700 border border-slate-200/80 rounded-bl-sm shadow-sm'"
        >
          <div class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
               :class="isSelf ? 'bg-primary-400' : 'bg-primary-50'"
          >
            <svg v-if="message.status === 'sending'" class="w-5 h-5 animate-spin" :class="isSelf ? 'text-white' : 'text-primary-500'" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <svg v-else class="w-5 h-5" :class="isSelf ? 'text-white' : 'text-primary-500'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
            </svg>
          </div>
          <div class="min-w-0">
            <p class="text-sm font-medium truncate">{{ message.fileName }}</p>
            <p class="text-[11px] mt-0.5" :class="isSelf ? 'text-primary-200' : 'text-slate-400'">
              <template v-if="message.status === 'sending'">上传中...</template>
              <template v-else-if="message.status === 'failed'"><span class="text-red-400">发送失败</span></template>
              <template v-else>{{ message.fileSize }}</template>
            </p>
          </div>
        </div>

        <!-- 产品卡片消息 -->
        <div
          v-else-if="message.type === 'product'"
          class="w-64 rounded-xl overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
          :class="isSelf
            ? 'bg-primary-500 text-white rounded-br-sm'
            : 'bg-white text-slate-700 border border-slate-200/80 rounded-bl-sm shadow-sm'"
          @click="handleProductClick"
        >
          <div class="flex items-start gap-3 p-3">
            <img
              v-if="message.productIcon"
              :src="message.productIcon"
              :alt="message.productName"
              class="w-12 h-12 rounded-lg object-cover flex-shrink-0"
            />
            <div v-else class="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-6 h-6 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-medium text-sm truncate">{{ message.productName }}</p>
              <p class="text-xs mt-0.5 line-clamp-2" :class="isSelf ? 'text-primary-100' : 'text-slate-400'">
                {{ message.productSummary }}
              </p>
            </div>
          </div>
          <div
            class="px-3 py-2 text-xs text-center border-t"
            :class="isSelf ? 'border-primary-100' : 'border-slate-100 text-primary-500'"
          >
            查看产品详情
          </div>
        </div>

        <!-- 图片消息 -->
        <div
          v-else-if="message.type === 'image'"
          class="rounded-xl overflow-hidden cursor-pointer relative"
          :class="isSelf ? 'rounded-br-sm' : 'rounded-bl-sm'"
          @click="message.status !== 'sending' && $emit('previewImage', resolvedImageUrl)"
        >
          <img :src="resolvedImageUrl" :alt="message.content || '图片'" class="max-w-[240px] max-h-[240px] object-cover" />
          <!-- 上传中遮罩 -->
          <div
            v-if="message.status === 'sending'"
            class="absolute inset-0 bg-black/30 flex items-center justify-center"
          >
            <svg class="w-7 h-7 text-white animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <!-- 上传失败 -->
          <div
            v-if="message.status === 'failed'"
            class="absolute inset-0 bg-black/40 flex items-center justify-center"
          >
            <div class="text-center">
              <svg class="w-6 h-6 text-red-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <span class="text-white text-xs mt-1 block">发送失败</span>
            </div>
          </div>
        </div>

        <!-- 悬停快捷操作 -->
        <div
          class="hidden group-hover:flex items-center gap-0.5"
          :class="isSelf ? 'flex-row-reverse' : 'flex-row'"
        >
          <button
            class="p-1 text-slate-400 hover:text-primary-500 hover:bg-slate-100 rounded transition-colors cursor-pointer"
            title="回复"
            @click="$emit('reply', message)"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
            </svg>
          </button>
          <button
            class="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors cursor-pointer"
            title="更多"
            @click="handleContextMenu($event)"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- 底部信息：时间 + 已读状态 -->
      <div class="flex items-center gap-1 mt-0.5 mx-1" :class="isSelf ? 'flex-row-reverse' : 'flex-row'">
        <span class="text-[10px] text-slate-400">{{ formatMsgTime(message.time) }}</span>
        <!-- 群聊已读回执（飞书风格） -->
        <ReadReceiptPopover
          v-if="isSelf && conversationType === 'group' && message.status !== 'sending' && message.status !== 'failed'"
          :message="message"
          :members="members"
          :current-user-id="currentUserId"
        />
        <!-- 单聊发送状态（保留原有勾号逻辑） -->
        <template v-else-if="isSelf && message.status && conversationType !== 'group'">
          <svg v-if="message.status === 'sent'" class="w-3 h-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
          <svg v-else-if="message.status === 'delivered'" class="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
          <svg v-else-if="message.status === 'read'" class="w-3 h-3 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
        </template>
      </div>
    </div>

    <!-- 自己的头像 -->
    <div
      v-if="isSelf"
      class="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-xs font-medium text-white flex-shrink-0 mt-0.5"
    >
      我
    </div>

    <!-- 右键菜单 -->
    <teleport to="body">
      <div
        v-if="showContextMenu"
        class="fixed inset-0 z-[100]"
        @click="showContextMenu = false"
        @contextmenu.prevent="showContextMenu = false"
      >
        <div
          class="absolute bg-white rounded-lg shadow-lg border border-slate-200 py-1 min-w-[120px] z-[101]"
          :style="{ left: menuPosition.x + 'px', top: menuPosition.y + 'px' }"
        >
          <button
            v-if="message.type === 'text'"
            class="w-full px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 text-left flex items-center gap-2 cursor-pointer"
            @click="handleCopy"
          >
            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
            </svg>
            复制
          </button>
          <button
            v-if="(message.type === 'file' || message.type === 'image') && message.status !== 'sending' && message.status !== 'failed'"
            class="w-full px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 text-left flex items-center gap-2 cursor-pointer"
            @click="handleDownload"
          >
            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
            </svg>
            下载
          </button>
          <button
            class="w-full px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 text-left flex items-center gap-2 cursor-pointer"
            @click="handleReply"
          >
            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
            </svg>
            引用回复
          </button>
          <button
            v-if="isSelf"
            class="w-full px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 text-left flex items-center gap-2 cursor-pointer"
            @click="handleDelete"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
            删除
          </button>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { useChatStore } from '@/stores/chat'
import ReadReceiptPopover from './ReadReceiptPopover.vue'

const props = defineProps({
  message: {
    type: Object,
    required: true
  },
  isSelf: {
    type: Boolean,
    default: false
  },
  showSender: {
    type: Boolean,
    default: false
  },
  avatarColor: {
    type: String,
    default: 'bg-slate-500'
  },
  conversationType: {
    type: String,
    default: ''
  },
  members: {
    type: Array,
    default: () => []
  },
  currentUserId: {
    type: [String, Number],
    default: ''
  },
  cachedUser: {
    type: Object,
    default: () => ({ name: '', avatar: '' })
  }
})

const emit = defineEmits(['reply', 'delete', 'previewImage'])

const chatStore = useChatStore()

// 优先使用缓存的用户信息，其次使用消息中的发送者名称
const displayName = computed(() => props.cachedUser?.name || props.message.senderName || '')
const displayAvatar = computed(() => props.cachedUser?.avatar || '')
// 图片消息：优先使用 IndexedDB 缓存的 blob URL
const resolvedImageUrl = computed(() => {
  if (props.message.type !== 'image') return ''
  return chatStore.getImageUrl(props.message.imageUrl)
})

const showContextMenu = ref(false)
const menuPosition = ref({ x: 0, y: 0 })

function handleContextMenu(e) {
  const menuWidth = 140
  const menuHeight = 120
  const x = Math.min(e.clientX, window.innerWidth - menuWidth)
  const y = Math.min(e.clientY, window.innerHeight - menuHeight)
  menuPosition.value = { x, y }
  showContextMenu.value = true
}

function handleCopy() {
  if (props.message.content) {
    navigator.clipboard.writeText(props.message.content).then(() => {
      ElMessage.success('已复制到剪贴板')
    }).catch(() => {
      ElMessage.error('复制失败')
    })
  }
  showContextMenu.value = false
}

function handleDownload() {
  const url = props.message.type === 'image' ? resolvedImageUrl.value : props.message.fileUrl
  if (!url) {
    ElMessage.warning('文件地址不可用')
    showContextMenu.value = false
    return
  }
  const a = document.createElement('a')
  a.href = url
  a.download = props.message.fileName || (props.message.type === 'image' ? '图片' : '文件')
  a.target = '_blank'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  showContextMenu.value = false
}

function handleReply() {
  emit('reply', props.message)
  showContextMenu.value = false
}

function handleDelete() {
  emit('delete', props.message.id)
  showContextMenu.value = false
}

function formatMsgTime(timeStr) {
  if (!timeStr) return ''
  const date = new Date(timeStr)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })
}

const router = useRouter()

function handleProductClick() {
  if (props.message.productId) {
    // 跳转到产品详情页
    router.push(`/product/detail/${props.message.productId}`)
  }
}
</script>
